import React, { useEffect, useState } from "react";
import { validateWithCodi } from "../../../../../components/codiOS/utils/validateWithCodi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditInfoForm = ({
  updatedProject,
  setUpdatedProject,
  fieldErrors,
  setFieldErrors,
  readOnly = false,
  isAdmin = false,
}) => {
  const [localErrors, setLocalErrors] = useState({});

  const handleInputChange = async (e) => {
    if (readOnly) return; // prevent editing in view-only mode
    const { name, value } = e.target;
    setUpdatedProject((prev) => ({ ...prev, [name]: value }));

    let err = "";

    if (name === "title") {
      if (!value.trim()) {
        err = "Project title is required.";
      } else {
        try {
          const res = await fetch(
            `${BACKEND_URL}/projects/check-title/${encodeURIComponent(value.toLowerCase())}`
          );
          const data = await res.json();
          if (data.exists && value !== updatedProject.title) {
            err = "This title is already taken. Please choose another.";
          }
        } catch {
          err = "Error checking title.";
        }
      }
    }

    if (name === "oneLiner") {
      const check = await validateWithCodi(value, "One-liner");
      if (!check.valid) err = check.reason || "One-liner lacks clarity.";
    }

    if (name === "description") {
      const check = await validateWithCodi(value, "Project Description");
      if (!check.valid) err = check.reason || "Description lacks clarity.";
    }

    setLocalErrors((prev) => ({ ...prev, [name]: err }));
    setFieldErrors((prev) => ({ ...prev, [name]: err }));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
      {/* Role awareness banner */}
      <div
        className={`text-sm text-center font-medium px-3 py-2 rounded-md mb-2 ${
          isAdmin
            ? "bg-green-50 text-green-700 border border-green-200"
            : "bg-gray-50 text-gray-600 border border-gray-200"
        }`}
      >
        {isAdmin
          ? "You can edit this project’s information."
          : "You are viewing this project in read-only mode."}
      </div>

      {/* Project Title */}
      <div className="flex flex-col space-y-1">
        <label htmlFor="title" className="text-sm font-semibold text-gray-700">
          Project Title
        </label>
        <input
          id="title"
          type="text"
          name="title"
          value={updatedProject?.title || ""}
          onChange={handleInputChange}
          readOnly={readOnly}
          disabled={readOnly}
          className={`w-full rounded-lg px-4 py-2 text-sm outline-none transition ${
            readOnly
              ? "bg-gray-100 text-gray-600 cursor-not-allowed"
              : localErrors?.title || fieldErrors?.title
              ? "bg-red-50 border border-red-500 focus:ring-red-500"
              : "bg-gray-100 focus:ring-blue-500"
          }`}
        />
        {(localErrors?.title || fieldErrors?.title) && !readOnly && (
          <p className="text-red-500 text-xs mt-1">
            {localErrors.title || fieldErrors.title}
          </p>
        )}
      </div>

      {/* One Liner */}
      <div className="flex flex-col space-y-1">
        <label htmlFor="oneLiner" className="text-sm font-semibold text-gray-700">
          One Liner
        </label>
        <input
          id="oneLiner"
          type="text"
          name="oneLiner"
          value={updatedProject?.oneLiner || ""}
          onChange={handleInputChange}
          readOnly={readOnly}
          disabled={readOnly}
          className={`w-full rounded-lg px-4 py-2 text-sm outline-none transition ${
            readOnly
              ? "bg-gray-100 text-gray-600 cursor-not-allowed"
              : localErrors?.oneLiner || fieldErrors?.oneLiner
              ? "bg-red-50 border border-red-500 focus:ring-red-500"
              : "bg-gray-100 focus:ring-blue-500"
          }`}
        />
        {(localErrors?.oneLiner || fieldErrors?.oneLiner) && !readOnly && (
          <p className="text-red-500 text-xs mt-1">
            {localErrors.oneLiner || fieldErrors.oneLiner}
          </p>
        )}
      </div>

      {/* Description */}
      <div className="flex flex-col space-y-1">
        <label htmlFor="description" className="text-sm font-semibold text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={updatedProject?.description || ""}
          onChange={handleInputChange}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder="Tell us more about this project..."
          className={`w-full rounded-lg px-4 py-2 text-sm outline-none min-h-[120px] resize-none transition ${
            readOnly
              ? "bg-gray-100 text-gray-600 cursor-not-allowed"
              : localErrors?.description || fieldErrors?.description
              ? "bg-red-50 border border-red-500 focus:ring-red-500"
              : "bg-gray-100 focus:ring-blue-500"
          }`}
        />
        {(localErrors?.description || fieldErrors?.description) && !readOnly && (
          <p className="text-red-500 text-xs mt-1">
            {localErrors.description || fieldErrors.description}
          </p>
        )}
      </div>
      {/* Last Updated Date */}
      {isAdmin && updatedProject?.updatedAt && !isNaN(new Date(updatedProject.updatedAt)) && (
        <p className="text-xs text-gray-400 text-right">
          Last updated:{" "}
          {new Date(updatedProject.updatedAt).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      )}

    </div>
  );
};

export default EditInfoForm;
